import { OTP_TTL_MS } from "@sajilni/config";
import { randomInt } from "node:crypto";
import { prisma, OTPType } from "client";
import { appleReviewStaticOtp, isAppleReviewOtp } from "./apple-review-otp";
import { isOtpExpired } from "./otp-ttl.pure";

export { appleReviewStaticOtp, isAppleReviewOtp } from "./apple-review-otp";
export { OTP_TTL_MS };
export { isOtpExpired };
export { getLoginEventsByEmailAndWhiteLabel } from "./login-events";
export { loginEventBucket } from "./login-events.pure";

/** Query strings treat `+` as a space (`ohoud+vis1@x.com` → `ohoud vis1@x.com`). */
export const normalizeQueryIdentifier = (identifier: string) =>
  identifier.replace(/ /g, "+").trim().toLowerCase();

export type OtpRecord = {
  id: string;
  code: number;
  type: OTPType;
  identifier: string;
  verified: boolean | null;
  createdAt: Date;
  updatedAt: Date;
};

export interface OtpStore {
  findUnique(args: {
    where: { identifier: string } | { id: string };
  }): Promise<OtpRecord | null>;
  create(args: {
    data: { code: number; type: OTPType; identifier: string };
  }): Promise<OtpRecord>;
  deleteMany(args: {
    where: Record<string, unknown>;
  }): Promise<{ count: number }>;
  updateMany(args: {
    where: Record<string, unknown>;
    data: { verified?: boolean; createdAt?: Date };
  }): Promise<{ count: number }>;
}

type OtpVersion = Pick<OtpRecord, "id" | "identifier" | "code" | "createdAt">;

const getDefaultOtpStore = () => prisma.oTP as unknown as OtpStore;

export interface GenerateOtpOptions {
  reviewOtp?: number | null;
  store?: OtpStore;
  codeFactory?: () => number;
  nowFactory?: () => Date;
}

const otpVersionWhere = (otp: OtpVersion) => ({
  id: otp.id,
  identifier: otp.identifier,
  code: otp.code,
  createdAt: otp.createdAt,
});

export const deleteOtpVersion = (
  otp: OtpVersion,
  store: OtpStore = getDefaultOtpStore()
) => store.deleteMany({ where: otpVersionWhere(otp) });

export const consumeOTP = async (
  otp: OtpVersion,
  store: OtpStore = getDefaultOtpStore()
) => {
  const consumed = await store.updateMany({
    where: {
      ...otpVersionWhere(otp),
      OR: [{ verified: false }, { verified: null }],
    },
    data: { verified: true },
  });
  if (consumed.count !== 1) return null;
  return store.findUnique({ where: { id: otp.id } });
};

const isUniqueConstraintError = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "code" in error &&
  error.code === "P2002";

// Generate a four-digit OTP. Codes are unique only per identifier: verification
// always requires both values, so different attendees can safely share a code.
export const generateOtpForIdentifier = async (
  identifier: string,
  options: GenerateOtpOptions = {}
) => {
  const {
    reviewOtp = null,
    store = getDefaultOtpStore(),
    codeFactory = () => randomInt(1000, 10_000),
    nowFactory = () => new Date(),
  } = options;
  const identifierType = identifier.includes("@")
    ? OTPType.EMAIL
    : OTPType.PHONE;

  // Reuse a live code so concurrent sends cannot deliver different codes out
  // of order. The unique identifier index elects one winner when no row exists.
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const existing = await store.findUnique({ where: { identifier } });
    const reusable =
      existing &&
      !existing.verified &&
      !isOtpExpired(existing.createdAt) &&
      (reviewOtp === null || existing.code === reviewOtp);
    if (reusable) {
      const renewedAt = nowFactory();
      const renewed = await store.updateMany({
        where: {
          ...otpVersionWhere(existing),
          OR: [{ verified: false }, { verified: null }],
        },
        data: { createdAt: renewedAt },
      });
      if (renewed.count !== 1) continue;

      const current = await store.findUnique({ where: { id: existing.id } });
      if (current) return current;
      continue;
    }

    if (existing) {
      await deleteOtpVersion(existing, store);
      continue;
    }

    try {
      return await store.create({
        data: {
          code: reviewOtp ?? codeFactory(),
          type: identifierType,
          identifier,
        },
      });
    } catch (error) {
      if (!isUniqueConstraintError(error)) throw error;
      // Another request created this identifier first. Re-read its winner.
    }
  }

  throw new Error("Unable to establish a current OTP after concurrent updates");
};

export const generateOTP = (identifier: string) =>
  generateOtpForIdentifier(identifier, {
    reviewOtp: appleReviewStaticOtp(identifier),
  });

// verify the otp
export const verifyOTP = async (identifier: string, code: number) => {
  // ponytail: bypass does not consume a row so reviewers can retry days later.
  // loginByOTP uses the same helper so login=true also works without a live DB row.
  if (isAppleReviewOtp(identifier, code)) {
    return {
      success: true,
      verifiedOtp: {
        id: "apple-review-bypass",
        code,
        type: OTPType.EMAIL,
        identifier: identifier.trim().toLowerCase(),
        verified: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    };
  }

  const otp = await prisma.oTP.findFirst({
    where: {
      code: code,
      identifier: identifier,
    },
  });
  if (!otp || otp.verified) {
    return {
      error: "Invalid OTP",
    };
  }
  if (isOtpExpired(otp.createdAt)) {
    const deleted = await deleteOtpVersion(otp);
    return deleted.count === 1
      ? { error: "OTP_EXPIRED" }
      : { error: "Invalid OTP" };
  }
  const verifiedOtp = await consumeOTP(otp);
  if (!verifiedOtp) return { error: "Invalid OTP" };

  return {
    success: true,
    verifiedOtp,
  };
};

//  delete the otp after verification
export const deleteOTP = async (identifier: string, code: number) => {
  const otp = await prisma.oTP.delete({
    where: {
      code: code,
      identifier: identifier,
    },
  });
  return otp;
};

// remove all otps with created date older than 5 minutes

export const removeOldOtps = async () => {
  const otps = await prisma.oTP.deleteMany({
    where: {
      createdAt: {
        lte: new Date(Date.now() - OTP_TTL_MS),
      },
    },
  });
  return otps;
};
